import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { copyToClipboard } from "lib/clipboard";

const CopyableRow = styled(Flex)(({ theme }) => ({
  padding: "8px 12px",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 4,
  backgroundColor: theme.colors.sysBackgroundAlternative,
}));

const CopyableValue = styled(Text)<{ wrapValue: boolean }>(({ wrapValue }) => ({
  wordBreak: wrapValue ? "break-all" : undefined,
}));

type Props = {
  value: string;
  toastMessage: string;
  /**
   * Wraps a value with no natural break points instead of letting it overflow. Off by default so
   * existing callers, which show URLs, keep breaking at the separators.
   */
  wrapValue?: boolean;
};

/**
 * A read-only value with a copy button — for things a user has to paste somewhere else, like a
 * callback URL or a generated credential.
 */
export function CopyableField({ value, toastMessage, wrapValue = false }: Props) {
  return (
    <CopyableRow alignItems="center" justifyContent="space-between" gap={8}>
      <CopyableValue as="code" wrapValue={wrapValue}>
        {value}
      </CopyableValue>
      <Button
        buttonType="tertiary"
        icon="copy01"
        onClick={async () => {
          await copyToClipboard({ plain: value });
          toast.success(toastMessage);
        }}
        aria-label={toastMessage}
      />
    </CopyableRow>
  );
}
